import {
  formatDateTime,
  formatMoney,
  type Product,
  type ProductInput,
  productInputSchema,
  routes,
} from '@pmai/contracts';
import {
  Button,
  type Column,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FormDialog,
  IconButton,
  TextAreaField,
  TextField,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../shared/api/hooks';
import { Can, usePermissions } from '../../shared/auth/session';
import { CardSection } from '../../shared/card-section';
import { bind } from '../../shared/forms/form';
import { useEntityForm } from '../../shared/forms/use-entity-form';
import { useConfirm } from '../../shared/use-confirm';

const invalidates = [routes.products.list];

/**
 * "Produkti" on the customer card (FR-CUST-011/012). The value is finance data — readers without
 * `finance.view` get no value from the API and see no value column (ADR 0004).
 */
export function CustomerProductsSection({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const products = useApiQuery(routes.products.list, { params: { id: customerId } });
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const remove = useConfirm(routes.products.remove, {
    invalidates,
    params: (p: Product) => ({ id: p.id }),
  });
  const f = (field: string) => t(`products.fields.${field}`);

  const columns: Column<Product>[] = [
    { key: 'name', header: f('name'), cell: (p) => p.name },
    ...(permissions.includes('finance.view')
      ? [
          {
            key: 'value',
            header: f('value'),
            align: 'right' as const,
            cell: (p: Product) => formatMoney(p.value),
          },
        ]
      : []),
    {
      key: 'comment',
      header: f('comment'),
      cell: (p) => <span className="whitespace-pre-line">{p.comment}</span>,
    },
    { key: 'createdAt', header: f('createdAt'), cell: (p) => formatDateTime(p.createdAt) },
    { key: 'updatedAt', header: f('updatedAt'), cell: (p) => formatDateTime(p.updatedAt) },
  ];
  if (permissions.includes('customers.edit'))
    columns.push({
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      cell: (p) => (
        <div className="flex justify-end gap-1">
          <IconButton icon="pencil" label={`${t('common.edit')}: ${p.name}`} onClick={() => setEditing(p)} />
          <IconButton
            icon="trash-2"
            label={`${t('common.delete')}: ${p.name}`}
            onClick={() => remove.ask(p)}
          />
        </div>
      ),
    });

  return (
    <CardSection
      id="customer.products"
      title={t('customers.sections.products')}
      actions={
        <Can perm="customers.edit">
          <Button size="sm" icon="plus" onClick={() => setEditing('new')}>
            {t('products.add')}
          </Button>
        </Can>
      }
    >
      <DataTable
        label={t('customers.sections.products')}
        rows={products.data}
        rowKey={(p) => p.id}
        loading={products.isPending}
        empty={<EmptyState icon="file-text" title={t('products.empty')} />}
        columns={columns}
      />
      <ProductDialog
        customerId={customerId}
        product={editing === 'new' ? null : editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        {...remove.dialog}
        title={t('products.deleteTitle')}
        message={t('products.deleteText', { name: remove.target?.name ?? '' })}
        confirmLabel={t('common.delete')}
      />
    </CardSection>
  );
}

const empty: ProductInput = { name: '', value: '', comment: '' };

function ProductDialog({
  customerId,
  product,
  open,
  onClose,
}: {
  customerId: string;
  product: Product | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { form, create, update, dialog } = useEntityForm({
    schema: productInputSchema,
    open,
    record: product,
    values: product
      ? { name: product.name, value: product.value ?? '', comment: product.comment ?? '' }
      : empty,
    create: routes.products.create,
    update: routes.products.update,
    invalidates,
    onClose,
  });

  const f = (field: string) => t(`products.fields.${field}`);
  return (
    <FormDialog
      {...dialog}
      title={product ? t('products.editTitle') : t('products.newTitle')}
      onSubmit={form.handleSubmit((body) =>
        product
          ? update.mutate({ params: { id: product.id }, body: { ...body, version: product.version } })
          : create.mutate({ params: { id: customerId }, body }),
      )}
    >
      <TextField label={f('name')} required {...bind(form, 'name')} />
      <TextField label={f('value')} inputMode="decimal" {...bind(form, 'value')} />
      <TextAreaField label={f('comment')} rows={3} {...bind(form, 'comment')} />
    </FormDialog>
  );
}
